import React, { useEffect, useState, useCallback } from "react";
import { Button, Spinner, Table, Modal } from "react-bootstrap";
import OmrService from "../../../_services/OmrService";

interface Props {
  omrTestId: number;
  onRefresh: () => void;
}

interface ScanRow {
  Id: number;
  OriginalFilename: string;
  ProcessingStatus: string;
  DetectedRollNumber: string | null;
  DetectedSetCode: string | null;
  MatchedStudentName: string | null;
  MatchedEnquiryId: number | null;
  MarksBreakdown: any;
  Errors: any;
  ReviewStatus: string | null;
  ThumbnailS3Url: string | null;
}

const STATUS_ICON: Record<string, { label: string; bg: string; color: string }> = {
  uploaded: { label: "Pending", bg: "#e2e3e5", color: "#383d41" },
  processing: { label: "Processing...", bg: "#ffeeba", color: "var(--status-warning-text)" },
  processed: { label: "Processed", bg: "#d4edda", color: "var(--status-success-text)" },
  error: { label: "Error", bg: "#f8d7da", color: "var(--status-danger-text)" },
};

const REVIEW_ICON: Record<string, { label: string; bg: string; color: string }> = {
  auto_ok: { label: "OK", bg: "#d4edda", color: "var(--status-success-text)" },
  needs_review: { label: "Review", bg: "#fff3cd", color: "var(--status-warning-text)" },
  reviewed: { label: "Reviewed", bg: "#cce5ff", color: "#004085" },
};

const ProcessReviewTab: React.FC<Props> = ({ omrTestId, onRefresh }) => {
  const [scans, setScans] = useState<ScanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [editScan, setEditScan] = useState<any>(null);

  const loadScans = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await OmrService.listScans(omrTestId);
      setScans(resp.data?.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [omrTestId]);

  useEffect(() => {
    loadScans();
  }, [loadScans]);

  const handleProcess = async () => {
    try {
      setProcessing(true);
      setMessage(null);
      const resp = await OmrService.processScans(omrTestId);
      const data = resp.data?.data;
      setMessage(`Processed: ${data?.processed || 0}, Errors: ${data?.errors || 0}, Need review: ${data?.reviewNeeded || 0}`);
      loadScans();
      onRefresh();
    } catch (err: any) {
      setMessage("Processing failed: " + (err.message || ""));
    } finally {
      setProcessing(false);
    }
  };

  const handleScanEdit = async (scanId: number) => {
    try {
      const resp = await OmrService.getScanDetail(omrTestId, scanId);
      setEditScan(resp.data?.data || null);
    } catch {
      // ignore
    }
  };

  const getMarksTotal = (scan: ScanRow) => {
    const bd = typeof scan.MarksBreakdown === "string" ? JSON.parse(scan.MarksBreakdown) : scan.MarksBreakdown;
    return bd ? `${bd.total}/${bd.maxMarks}` : "—";
  };

  const getErrorCount = (scan: ScanRow) => {
    const errors = typeof scan.Errors === "string" ? JSON.parse(scan.Errors) : scan.Errors;
    return errors?.length || 0;
  };

  const pendingCount = scans.filter((s) => s.ProcessingStatus === "uploaded").length;

  return (
    <div>
      {message && (
        <div style={{
          padding: "6px 12px", borderRadius: 4, marginBottom: 12,
          background: message.includes("failed") ? "#f8d7da" : "#d4edda",
          color: message.includes("failed") ? "#721c24" : "#155724",
        }}>
          {message}
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <Button variant="primary" size="sm" onClick={handleProcess} disabled={processing || pendingCount === 0}>
          {processing ? <><Spinner animation="border" size="sm" /> Processing...</> : `Process All (${pendingCount} pending)`}
        </Button>
        <Button variant="secondary" size="sm" onClick={loadScans} disabled={loading}>
          Refresh
        </Button>
        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
          {scans.length} total scan(s)
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" /></div>
      ) : scans.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: "var(--text-faint)" }}>No scans uploaded yet. Go to "Upload Scans" tab first.</div>
      ) : (
        <Table bordered hover size="sm" style={{ fontSize: "0.8rem" }}>
          <thead>
            <tr style={{ background: "var(--surface-page)" }}>
              <th style={{ width: 30 }}>#</th>
              <th>File</th>
              <th>Roll No</th>
              <th>Name</th>
              <th>Set</th>
              <th>Marks</th>
              <th>Status</th>
              <th>Review</th>
              <th>Errors</th>
              <th style={{ width: 60 }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {scans.map((scan, idx) => {
              const st = STATUS_ICON[scan.ProcessingStatus] || STATUS_ICON.uploaded;
              const rv = scan.ReviewStatus ? (REVIEW_ICON[scan.ReviewStatus] || REVIEW_ICON.auto_ok) : null;
              const errCount = getErrorCount(scan);

              return (
                <tr key={scan.Id}>
                  <td>{idx + 1}</td>
                  <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{scan.OriginalFilename}</td>
                  <td>{scan.DetectedRollNumber || "—"}</td>
                  <td>{scan.MatchedStudentName || (scan.DetectedRollNumber && !scan.MatchedEnquiryId ? <span style={{ color: "var(--status-danger)" }}>Unmatched</span> : "—")}</td>
                  <td>{scan.DetectedSetCode || "—"}</td>
                  <td>{getMarksTotal(scan)}</td>
                  <td>
                    <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: "0.7rem", background: st.bg, color: st.color }}>
                      {st.label}
                    </span>
                  </td>
                  <td>
                    {rv && (
                      <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: "0.7rem", background: rv.bg, color: rv.color }}>
                        {rv.label}
                      </span>
                    )}
                  </td>
                  <td style={{ color: errCount > 0 ? "#dc3545" : "#28a745" }}>
                    {errCount > 0 ? errCount : "0"}
                  </td>
                  <td>
                    {scan.ProcessingStatus === "processed" && (
                      <Button variant="secondary" size="sm" style={{ fontSize: "0.7rem", padding: "1px 6px" }}
                        onClick={() => handleScanEdit(scan.Id)}>
                        View
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Scan Editor Modal */}
      {editScan && (
        <ScanEditorModal
          scan={editScan}
          omrTestId={omrTestId}
          onClose={() => { setEditScan(null); loadScans(); }}
        />
      )}
    </div>
  );
};

// ── Scan Editor Modal ────────────────────────────────────────────────

const ScanEditorModal: React.FC<{ scan: any; omrTestId: number; onClose: () => void }> = ({ scan, omrTestId, onClose }) => {
  const [rollNumber, setRollNumber] = useState(scan.DetectedRollNumber || "");
  const [setCode, setSetCode] = useState(scan.DetectedSetCode || "");
  const [saving, setSaving] = useState(false);

  const answers = typeof scan.DetectedAnswers === "string" ? JSON.parse(scan.DetectedAnswers) : (scan.DetectedAnswers || []);
  const errors = typeof scan.Errors === "string" ? JSON.parse(scan.Errors) : (scan.Errors || []);
  const breakdown = typeof scan.MarksBreakdown === "string" ? JSON.parse(scan.MarksBreakdown) : scan.MarksBreakdown;

  const handleSave = async () => {
    try {
      setSaving(true);
      await OmrService.updateScan(omrTestId, scan.Id, {
        rollNumber: rollNumber !== scan.DetectedRollNumber ? rollNumber : undefined,
        setCode: setCode !== scan.DetectedSetCode ? setCode : undefined,
        reviewStatus: "reviewed",
      });
      onClose();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>Scan: {scan.OriginalFilename}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", gap: 20 }}>
          {/* Left: scan image */}
          <div style={{ flex: "0 0 300px" }}>
            {scan.S3Url ? (
              <img src={scan.S3Url} alt="Scan" style={{ width: "100%", border: "1px solid var(--border-default)", borderRadius: 4 }} />
            ) : (
              <div style={{ padding: 20, background: "var(--surface-sunken)", textAlign: "center", color: "var(--text-faint)" }}>No preview</div>
            )}
          </div>

          {/* Right: detected data */}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: "bold" }}>Roll Number</label>
                <input className="form-control form-control-sm" value={rollNumber}
                  onChange={(e) => setRollNumber(e.target.value)} style={{ width: 120 }} />
              </div>
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: "bold" }}>Set</label>
                <select className="form-control form-control-sm" value={setCode}
                  onChange={(e) => setSetCode(e.target.value)} style={{ width: 60 }}>
                  <option value="">—</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: "bold" }}>Student</label>
                <div style={{ fontSize: "0.85rem", paddingTop: 4 }}>
                  {scan.MatchedStudentName || <span style={{ color: "var(--status-danger)" }}>Not matched</span>}
                </div>
              </div>
            </div>

            {/* Marks breakdown */}
            {breakdown && (
              <div style={{ fontSize: "0.8rem", marginBottom: 12, padding: 8, background: "var(--surface-page)", borderRadius: 4 }}>
                <strong>Total: {breakdown.total}/{breakdown.maxMarks}</strong> | Correct: {breakdown.correct} | Wrong: {breakdown.wrong} | Unattempted: {breakdown.unattempted}
                {breakdown.sections?.map((s: any) => (
                  <span key={s.name} style={{ marginLeft: 12 }}>| {s.name}: {s.marks}/{s.total}</span>
                ))}
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div style={{ fontSize: "0.75rem", marginBottom: 8, padding: 6, background: "var(--status-danger-bg)", color: "var(--status-danger-text)", borderRadius: 4 }}>
                {errors.map((e: any, i: number) => <div key={i}>{e.type}: {e.details}</div>)}
              </div>
            )}

            {/* Answers grid */}
            <div style={{ maxHeight: 300, overflow: "auto", fontSize: "0.7rem" }}>
              <table className="table table-bordered table-sm" style={{ width: "auto" }}>
                <thead>
                  <tr style={{ background: "var(--surface-page)" }}>
                    <th>Q</th><th>Answer</th><th>Fill Ratios</th>
                  </tr>
                </thead>
                <tbody>
                  {answers.map((a: any) => (
                    <tr key={a.q} style={{ background: a.confidence < 0.8 ? "#fff3cd" : undefined }}>
                      <td>{a.q}</td>
                      <td style={{ fontWeight: "bold" }}>{a.answer || "—"}</td>
                      <td style={{ color: "var(--text-faint)" }}>{a.fillRatios?.map((r: number) => r.toFixed(2)).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save & Mark Reviewed"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProcessReviewTab;
